/**
 * AIストラテジー - Hard（むずかしい）
 *
 * - Normal をベースに拡張
 * - シナジーを考慮した召喚（将来拡張）
 * - ダメージ期待値を最大化する攻撃
 * - トリガー・刹那詠唱を積極的に使用
 */

import { shouldSkipAttackOnMonster } from './base';
import type { AIStrategy, AIGameState, UsableSkill, ChargeableMonster, ChainContext } from './base';
import { normalStrategy } from './normal';
import { isPhaseCardChargeRecommended } from '../phaseCardChargeChecker';
import type { CardWithId, MonsterInstance, HandCard } from '../../../types/card';
import type { DeepSeaCard } from '../../deepSeaZone/types';
import {
  hasHikou,
  isGroundMonster,
  getCounterAttackMultiplier,
  getDirectAttackStatus,
} from '../../keywordAbilities';

// =============================================================================
// 刹那詠唱カードID定義（効果タイプ別）
// =============================================================================

/**
 * 攻撃宣言時に発動すべき刹那詠唱カード（防御/妨害系）
 * - 行動不能、ATK減少、攻撃先変更、コントロール奪取、守護付与
 */
const ATTACK_RESPONSE_SETSUNA_IDS = new Set([
  // 行動不能系（攻撃を止める）
  'C0000013', // 粘液の罠 - ATK800↓、行動不能
  'C0000075', // シャドウ・バインド - 1ターン行動不能
  'C0000176', // 熔岩の罠 - ATK半減、行動不能
  'C0000238', // おしおきの鎖 - ATK800↓、行動不能
  'C0000243', // ご主人様のおしおき - ATK0、行動不能、味方ATK1000↑
  'C0000389', // 黒呪・ザルガノートの呪鎖 - 行動不能、毎ターン400ダメ
  'C0000402', // 呪術狩りの封印鎖 - 行動不能、闇属性ならATK0

  // ATK大幅減少系（実質攻撃無効化）
  'C0000087', // シャドウ・チェインズ - ATK0、400ダメ
  'C0000150', // 氷の吐息 - ATK800↓、凍結
  'C0000226', // 順番待ちの苛立ち - ATK2000↓
  'C0000288', // 酸の霧 - ATK0

  // 攻撃先変更（同士討ち）
  'C0000048', // 人魚の波誘い - 攻撃先変更
  'C0000178', // 狸火の誘い - 攻撃先変更、ATK500↓

  // コントロール奪取
  'C0000153', // 氷猫の遊戯 - コントロール奪取
  'C0000237', // 幼魔王女の誘惑 - コントロール奪取

  // 守護付与（防御）
  'C0000391', // 黒呪・トゥルヴァントの障壁 - 守護付与
]);

/**
 * ダメージ/破壊系の刹那詠唱カード
 * - 攻撃宣言時またはバトル開始時に有効
 */
const DAMAGE_SETSUNA_IDS = new Set([
  // ダメージ系
  'C0000022', // 火竜の吐息 - 1000ダメ、プレイヤー500ダメ
  'C0000032', // ドラゴンの炎息 - 800ダメ、全体300ダメ
  'C0000065', // 天使の波動 - 光モンスター数×300全体ダメ
  'C0000133', // 鎖の舞踏 - ATK0、毎ターン300プレイヤーダメ
  'C0000136', // 鎖の呪縛 - 1500ダメ
  'C0000194', // 急ごしらえの罠 - ATK500↓、500ダメ
  'C0000254', // 時の歪曲 - ATK0、条件付き2000ダメ
  'C0000323', // 雷鳴の裁き - ATK1200↓、攻撃不能、全体300ダメ
  'C0000349', // アヴィクルスの裁定 - ATK1000↓、行動不能、条件付き500ダメ
  'C0000388', // 黒呪・ファルザノンの影爪 - 1000ダメ
  'C0000390', // 黒呪・ヴェルザクトの黒刃 - 1200ダメ、ATK500↓
  'C0000429', // 黒涙のうねり - HP1000↓、ATK300↓

  // 破壊系
  'C0000015', // 粘液の浸食 - 毎ターンATK500↓、0で破壊
  'C0000064', // ルミナスの裁き - 光以外を破壊、回復
  'C0000209', // 即興の連携 - ATK800↑、戦闘ダメで破壊
]);

/**
 * バトル開始時のみ発動すべき刹那詠唱カード
 * - 手札回収、デッキ操作、効果無効など
 */
const BATTLE_START_ONLY_SETSUNA_IDS = new Set([
  'C0000050', // クラーケンの呼び声 - 墓地→手札、コスト-1
  'C0000277', // 星屑の時導術 - デッキトップ3枚操作
  'C0000210', // 静寂の帳 - 全モンスター効果無効
  'C0000217', // 光果の舞踏 - 自モンスター破壊→プラント召喚
  'C0000431', // 灰塵の刻印 - 攻撃不能、被ダメ+300
]);

export const hardStrategy: AIStrategy = {
  ...normalStrategy, // Normal をベースに拡張

  // コスト効率 + フィールド状況を考慮
  chooseSummon(summonableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (summonableCards.length === 0) return null;

    // フィールドにモンスターがいない場合は積極的に召喚
    const myMonsterCount = gameState.myField.filter(m => m).length;
    const opponentMonsterCount = gameState.opponentField.filter(m => m).length;

    // 相手より少ない場合は高コストカードも積極的に
    if (myMonsterCount < opponentMonsterCount) {
      // 攻撃力が高いカードを優先
      const sorted = [...summonableCards].sort((a, b) => {
        return (b.attack || 0) - (a.attack || 0);
      });
      return sorted[0];
    }

    // 通常はコスト効率で判断
    return normalStrategy.chooseSummon(summonableCards, gameState);
  },

  // ダメージ効率を最大化する攻撃対象選択（反撃死・飛行システムを考慮）
  chooseAttackTarget(validTargets: (number | 'direct')[], attacker: MonsterInstance, gameState: AIGameState): number | 'direct' {
    if (validTargets.length === 0) return 'direct';

    const monsterTargets = validTargets.filter(t => t !== 'direct');
    const attackPower = attacker.currentAttack || attacker.attack || 0;
    const attackerHp = attacker.currentHp || attacker.hp || 0;
    const attackerHasFlight = hasHikou(attacker);

    // 相手フィールドにモンスターがいない場合は直接攻撃
    if (monsterTargets.length === 0) {
      return 'direct';
    }

    // 各ターゲットの評価（飛行システム対応）
    const evaluatedTargets = monsterTargets.map(idx => {
      const target = gameState.opponentField[idx];
      if (!target) return { idx, score: -Infinity, shouldSkip: true };

      const targetHp = target.currentHp || 0;
      const targetAttack = target.currentAttack || 0;
      const canKill = targetHp <= attackPower;

      // 飛行システム: 反撃ダメージ倍率を取得
      const counterMultiplier = getCounterAttackMultiplier(attacker, target);
      const counterDamage = Math.floor(targetAttack * counterMultiplier);
      const noCounter = counterMultiplier === 0;
      const willDieFromCounter = counterDamage >= attackerHp;

      // 攻撃スキップ判断（飛行対応済み）
      const shouldSkip = shouldSkipAttackOnMonster(attacker, target, gameState);

      // スコア計算（飛行システム考慮）
      let score = 0;

      if (canKill) {
        score += 1000; // 倒せるなら大きなボーナス
        score += targetAttack; // 脅威度の高いモンスターを優先
      } else {
        score += Math.min(attackPower, targetHp); // 与えられるダメージ
        score += targetAttack * 0.5; // 脅威度考慮
      }

      // 飛行ボーナス: 反撃なし攻撃を優先
      if (noCounter) {
        score += 500; // 安全な攻撃にボーナス
      }

      // 飛行ペナルティ: 反撃で死ぬ可能性があるなら減点
      if (!noCounter && willDieFromCounter && !canKill) {
        score -= 800; // 一方的に倒される攻撃はペナルティ
      }

      // 地上モンスター優先（飛行攻撃者の場合）
      if (attackerHasFlight && isGroundMonster(target)) {
        score += 200; // 地上モンスターを優先（反撃なし）
      }

      return { idx, score, canKill, targetAttack, shouldSkip, noCounter };
    });

    // スキップしないターゲットのみ抽出
    const validAttackTargets = evaluatedTargets.filter(t => !t.shouldSkip);

    // 全てスキップ対象なら直接攻撃を検討
    if (validAttackTargets.length === 0) {
      // 直接攻撃の条件を飛行システムで評価
      if (validTargets.includes('direct')) {
        const directStatus = getDirectAttackStatus(attacker, gameState.opponentField);
        if (directStatus.canDirect) {
          return 'direct';
        }
      }
      // 直接攻撃も不可なら最も安全なターゲットを選択
      const safest = evaluatedTargets
        .filter(t => t.noCounter)
        .sort((a, b) => b.score - a.score)[0];
      if (safest) return safest.idx;
      return 'direct';
    }

    // スコア順にソート
    validAttackTargets.sort((a, b) => b.score - a.score);

    // 直接攻撃との比較（飛行システム考慮）
    const bestTarget = validAttackTargets[0];
    if (bestTarget && bestTarget.score > 0) {
      // 直接攻撃が可能な場合の比較
      if (validTargets.includes('direct')) {
        const directStatus = getDirectAttackStatus(attacker, gameState.opponentField);
        if (directStatus.canDirect) {
          const directDamage = Math.floor(attackPower * directStatus.damageMultiplier);
          // 直接攻撃で勝てる場合
          if (directDamage >= gameState.opponentLife) {
            return 'direct';
          }
          // 飛行の安全なダイレクト（反撃なし、50%ダメージ）
          if (attackerHasFlight && directStatus.counterMultiplier === 0) {
            // モンスターを倒せない場合、ダイレクトも検討
            if (!bestTarget.canKill && directDamage >= bestTarget.score * 0.3) {
              return 'direct';
            }
          }
        }
      }
      return bestTarget.idx;
    }

    return 'direct';
  },

  // スキルを積極的に使用
  chooseSkill(usableSkills: UsableSkill[], gameState: AIGameState): UsableSkill | null {
    if (usableSkills.length === 0) return null;

    // 上級技があれば必ず使用
    const advancedSkills = usableSkills.filter(s => s.skillType === 'advanced');
    if (advancedSkills.length > 0) {
      return advancedSkills[0];
    }

    // 基本技は60%の確率で使用
    if (Math.random() < 0.6) {
      return usableSkills[0];
    }

    return null;
  },

  // 戦略的なチャージ判断
  chooseCharge(chargeableCards: CardWithId[], chargeableMonsters: ChargeableMonster[], gameState: AIGameState): { card: CardWithId; monsterIndex: number } | null {
    if (chargeableCards.length === 0 || chargeableMonsters.length === 0) return null;

    // チャージが0のモンスター
    const noChargeMonsters = chargeableMonsters.filter(m => m.currentCharges === 0);
    // チャージが1のモンスター
    const oneChargeMonsters = chargeableMonsters.filter(m => m.currentCharges === 1);

    let targetMonster = null;

    // 上級技を持つモンスターで、チャージが1のものを優先（すぐ上級技が使える）
    const oneChargeWithAdvanced = oneChargeMonsters.filter(m => m.monster.advancedSkill);
    if (oneChargeWithAdvanced.length > 0) {
      // 攻撃力が高いものを選択
      oneChargeWithAdvanced.sort((a, b) =>
        (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
      );
      targetMonster = oneChargeWithAdvanced[0];
    } else if (noChargeMonsters.length > 0) {
      // チャージ0のモンスターから選択
      // 基本技を持つモンスターを優先
      const withBasic = noChargeMonsters.filter(m => m.monster.basicSkill);
      if (withBasic.length > 0) {
        // 攻撃力が高いものを選択
        withBasic.sort((a, b) =>
          (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
        );
        targetMonster = withBasic[0];
      } else {
        // 上級技のみ持つモンスター（基本技なし）にもチャージ
        noChargeMonsters.sort((a, b) =>
          (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
        );
        targetMonster = noChargeMonsters[0];
      }
    } else if (oneChargeMonsters.length > 0) {
      // チャージ1のモンスターに追加チャージ（上級技のため）
      oneChargeMonsters.sort((a, b) =>
        (b.monster.currentAttack || b.monster.attack || 0) - (a.monster.currentAttack || a.monster.attack || 0)
      );
      targetMonster = oneChargeMonsters[0];
    }

    if (!targetMonster) return null;

    // チャージに使うカードはモンスターと同じ属性のものを選択
    const targetAttribute = targetMonster.monster.attribute;
    const sameAttributeCards = chargeableCards.filter(card => card.attribute === targetAttribute);

    if (sameAttributeCards.length === 0) return null; // 同属性カードがなければチャージしない

    // コストが低いカードを優先するが、召喚に使いたいカードは温存
    const sortedCards = [...sameAttributeCards].sort((a, b) => {
      // 魔法カードやフィールドカードを優先（モンスターは温存）
      if (a.type !== 'monster' && b.type === 'monster') return -1;
      if (a.type === 'monster' && b.type !== 'monster') return 1;
      // 同じタイプならコストが低いものを優先
      return (a.cost || 0) - (b.cost || 0);
    });
    const cardToCharge = sortedCards[0];

    return {
      card: cardToCharge,
      monsterIndex: targetMonster.monsterIndex,
    };
  },

  // トリガーを積極的に発動
  chooseTrigger(activatableTriggers: unknown[], gameState: AIGameState): unknown | null {
    if (activatableTriggers.length === 0) return null;
    // 80%の確率で発動
    if (Math.random() < 0.8) {
      return activatableTriggers[0];
    }
    return null;
  },

  // 刹那詠唱を状況に応じて発動（相手ターン中のバトルフェイズで発動）
  chooseSetsuna(availableSetsunaCards: CardWithId[], chainContext: ChainContext, gameState: AIGameState): CardWithId | null {
    if (availableSetsunaCards.length === 0) return null;

    // 戦闘に影響するカードを事前に検索
    const attackResponseCard = availableSetsunaCards.find(c =>
      ATTACK_RESPONSE_SETSUNA_IDS.has(c.id)
    );
    const damageCard = availableSetsunaCards.find(c =>
      DAMAGE_SETSUNA_IDS.has(c.id)
    );
    const battleStartCard = availableSetsunaCards.find(c =>
      BATTLE_START_ONLY_SETSUNA_IDS.has(c.id)
    );

    // 攻撃宣言時: 攻撃レスポンス系/ダメージ系を100%発動
    if (chainContext.attacker) {
      // 攻撃レスポンス系を優先（行動不能、ATK減少、攻撃先変更、コントロール奪取、守護）
      if (attackResponseCard) return attackResponseCard;
      // ダメージ/破壊系
      if (damageCard) return damageCard;
    }

    // バトル開始時
    if (chainContext.type === 'battleStart') {
      // 戦闘に影響するカード（攻撃レスポンス/ダメージ系）があるなら温存
      // → バトル開始時専用カード（墓地回収、デッキ操作等）は発動しない
      const hasCombatCard = attackResponseCard || damageCard;

      if (!hasCombatCard && battleStartCard) {
        // 戦闘用カードがなければバトル開始時専用カードを発動
        return battleStartCard;
      }

      // ダメージ系はバトル開始時でも発動可（攻撃宣言時まで待たなくてもいい）
      if (damageCard) {
        return damageCard;
      }
    }

    return null;
  },

  // 魔法カードを状況に応じて使用
  chooseMagicCard(usableMagicCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (usableMagicCards.length === 0) return null;

    // ダメージ系・回復系を優先
    const priorityCards = usableMagicCards.filter(c =>
      c.effect?.includes('ダメージ') ||
      c.effect?.includes('回復') ||
      c.effect?.includes('ドロー')
    );

    if (priorityCards.length > 0) {
      return priorityCards[0];
    }

    // それ以外は50%の確率で使用
    if (Math.random() < 0.5) {
      return usableMagicCards[0];
    }

    return null;
  },

  // フィールドカード配置（戦略的判断・スコアリング）
  chooseFieldCard(placeableFieldCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (placeableFieldCards.length === 0) return null;

    const myMonsters = gameState.myField.filter(m => m).length;
    const opponentMonsters = gameState.opponentField.filter(m => m).length;
    const turn = gameState.turn || 1;

    // 相手にモンスターが2体以上いて、こちらにいない → モンスター優先
    if (opponentMonsters >= 2 && myMonsters === 0) return null;

    // 各フィールドカードをスコアリング
    const scored = placeableFieldCards.map(fieldCard => {
      let score = 0;
      const effect = fieldCard.effect || '';

      // === 序盤ボーナス（累積効果は早期配置が有利）===
      if (turn <= 2) score += 30;
      else if (turn <= 4) score += 15;

      // === コスト軽減効果 → 最優先 ===
      if (effect.includes('コスト') && (effect.includes('軽減') || effect.includes('-'))) {
        score += 50;
      }

      // === 手札にシナジーモンスターがいるか ===
      const synergisticMonstersInHand = gameState.myHand.filter(card => {
        if (card.type !== 'monster') return false;
        // 属性一致
        if (card.attribute === fieldCard.attribute) return true;
        // カテゴリ一致（《ドラゴン》《ブリザードキャット》等）
        if (effect.includes('《') && card.category) {
          const categoryMatch = effect.match(/《([^》]+)》/);
          if (categoryMatch && card.category.includes(categoryMatch[1])) return true;
        }
        return false;
      });
      score += synergisticMonstersInHand.length * 20;

      // === フィールドにシナジーモンスターがいるか ===
      const synergisticMonstersOnField = gameState.myField.filter(monster => {
        if (!monster) return false;
        if (monster.attribute === fieldCard.attribute) return true;
        // カテゴリ一致
        if (effect.includes('《') && monster.category) {
          const categoryMatch = effect.match(/《([^》]+)》/);
          if (categoryMatch && monster.category.includes(categoryMatch[1])) return true;
        }
        return false;
      });
      score += synergisticMonstersOnField.length * 25;

      // === エンドフェイズダメージ効果 ===
      if (effect.includes('エンドフェイズ') && effect.includes('ダメージ')) {
        score += opponentMonsters * 10; // 相手モンスターが多いほど有効
        score += 10; // 基礎点
      }

      // === SP回復効果 ===
      if (effect.includes('SP') && (effect.includes('回復') || effect.includes('アクティブ'))) {
        score += 20;
      }

      // === 攻撃力アップ効果 ===
      if (effect.includes('攻撃力') && effect.includes('アップ')) {
        score += (synergisticMonstersOnField.length + synergisticMonstersInHand.length) * 10;
      }

      return { card: fieldCard, score };
    });

    // スコア順にソート
    scored.sort((a, b) => b.score - a.score);

    // スコア40以上なら配置（閾値で判断）
    if (scored[0] && scored[0].score >= 40) {
      return scored[0].card;
    }

    // スコア40未満でも、他にやることがなければ配置する
    // （召喚可能なモンスターも使用可能な魔法もない場合）
    const availableSP = gameState.myActiveSP + (gameState.myBonusSP || 0);
    const hasSummonableMonster = gameState.myHand.some(card =>
      card.type === 'monster' && card.cost <= availableSP
    );
    const hasUsableMagic = gameState.myHand.some(card =>
      card.type === 'magic' && card.cost <= availableSP
    );
    const hasEmptySlot = gameState.myField.some(slot => !slot);

    // モンスターも魔法も出せない、かつフィールドカードがあるなら配置
    if (!hasSummonableMonster && !hasUsableMagic && scored[0]) {
      return scored[0].card;
    }
    // モンスターがいるが空きスロットがない場合も配置
    if (hasSummonableMonster && !hasEmptySlot && scored[0]) {
      return scored[0].card;
    }

    return null;
  },

  // フェイズカード配置（戦略的判断・スコアリング）
  choosePhaseCard(placeablePhaseCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (placeablePhaseCards.length === 0) return null;

    const myMonsters = gameState.myField.filter(m => m).length;
    const opponentMonsters = gameState.opponentField.filter(m => m).length;
    const turn = gameState.turn || 1;

    // 相手にモンスターが2体以上いて、こちらにいない → モンスター優先
    if (opponentMonsters >= 2 && myMonsters === 0) return null;

    // 各フェイズカードをスコアリング
    const scored = placeablePhaseCards.map(phaseCard => {
      let score = 0;
      const effect = phaseCard.effect || '';
      const isNoneAttribute = phaseCard.attribute === 'なし';

      // === 序盤ボーナス（段階強化のため早期配置が有利）===
      if (turn <= 2) score += 35;
      else if (turn <= 4) score += 20;

      // === コスト軽減効果 → 最優先 ===
      if (effect.includes('コスト') && (effect.includes('軽減') || effect.includes('-'))) {
        score += 50;
      }

      // === 手札にシナジーカードがいるか（全カード種類対象）===
      // 同属性 or フェイズカードが「なし」属性なら全カード
      const synergisticCardsInHand = gameState.myHand.filter(card => {
        if (card.uniqueId === phaseCard.uniqueId) return false;
        // 「なし」属性フェイズカードなら全カードがシナジー
        if (isNoneAttribute) return true;
        // 同属性チェック
        if (card.attribute === phaseCard.attribute) return true;
        // カテゴリ一致
        if (effect.includes('《') && card.category) {
          const categoryMatch = effect.match(/《([^》]+)》/);
          if (categoryMatch && card.category.includes(categoryMatch[1])) return true;
        }
        return false;
      });
      score += synergisticCardsInHand.length * 15;

      // === フィールドにシナジーモンスターがいるか ===
      // 同属性 or フェイズカードが「なし」属性なら全モンスター
      const synergisticMonstersOnField = gameState.myField.filter(monster => {
        if (!monster) return false;
        if (isNoneAttribute) return true;
        if (monster.attribute === phaseCard.attribute) return true;
        return false;
      });
      score += synergisticMonstersOnField.length * 20;

      // === 手札にチャージ用カードがあるか（段階強化用）===
      // 同属性 or 「なし」属性 or フェイズカードが「なし」属性なら全カード
      const chargeableCards = gameState.myHand.filter(card => {
        if (card.uniqueId === phaseCard.uniqueId) return false;
        // フェイズカードが「なし」属性なら全カードチャージ可能
        if (isNoneAttribute) return true;
        // 同属性 or 「なし」属性カード
        return card.attribute === phaseCard.attribute || card.attribute === 'なし';
      });
      score += chargeableCards.length * 15;

      return { card: phaseCard, score, chargeableCount: chargeableCards.length };
    });

    // スコア順にソート
    scored.sort((a, b) => b.score - a.score);

    // スコア30以上なら配置
    if (scored[0] && scored[0].score >= 30) {
      return scored[0].card;
    }

    // フォールバック: チャージ用カード3枚以上なら配置
    if (scored[0] && scored[0].chargeableCount >= 3) {
      return scored[0].card;
    }

    return null;
  },

  // フェイズカードチャージ（戦略的選択）
  choosePhaseCardCharge(phaseCardChargeableCards: CardWithId[], gameState: AIGameState): CardWithId | null {
    if (phaseCardChargeableCards.length === 0) return null;

    // フェイズカードの現在の段階を確認
    const phaseCard = gameState.myPhaseCard;
    const currentStage = phaseCard?.stage !== undefined ? phaseCard.stage : (phaseCard?.charges?.length || 0);

    // 2→3段階目（最終段階）へのチャージは条件チェック
    if (currentStage === 2) {
      if (!isPhaseCardChargeRecommended(phaseCard, gameState)) {
        // 3段階目効果が有効でない場合はチャージしない
        return null;
      }
    }

    // カード選択の優先度:
    // 1. 魔法カード・フィールドカード・フェイズカード（モンスター温存）
    // 2. コストが低いモンスター（高コストモンスター温存）
    const sorted = [...phaseCardChargeableCards].sort((a, b) => {
      // モンスター以外を優先
      const aIsMonster = a.type === 'monster' ? 1 : 0;
      const bIsMonster = b.type === 'monster' ? 1 : 0;
      if (aIsMonster !== bIsMonster) return aIsMonster - bIsMonster;

      // 同じタイプならコストが低いものを優先
      return (a.cost || 0) - (b.cost || 0);
    });

    // 最終段階（2→3）の場合は条件を満たしているので積極的にチャージ
    if (currentStage === 2) {
      return sorted[0];
    }

    // 1→2段階目: 魔法/フィールド/フェイズカードがあれば優先的にチャージ
    const nonMonsterCard = sorted.find(c => c.type !== 'monster');
    if (nonMonsterCard) {
      return nonMonsterCard;
    }

    // モンスターしかない場合: コスト2以下のモンスターなら使用
    const cheapMonster = sorted.find(c => c.type === 'monster' && (c.cost || 0) <= 2);
    if (cheapMonster) {
      return cheapMonster;
    }

    // 手札に4枚以上あるならモンスターでもチャージ（リソースに余裕あり）
    if (gameState.myHand.length >= 4) {
      return sorted[0];
    }

    return null;
  },

  // =============================================================================
  // 深海領域アクション
  // =============================================================================

  /**
   * 潜航するカードを選択
   *
   * 戦略:
   * - フィールドに空きがある場合は召喚優先（潜航は温存）
   * - SPが不足している場合に高コストモンスターを潜航
   * - 深度ボーナス（ATK+200/深度）を考慮
   */
  chooseSubmerge(submergibleCards: HandCard[], gameState: AIGameState): HandCard | null {
    if (submergibleCards.length === 0) return null;

    const emptySlots = gameState.myField.filter(m => m === null).length;
    const totalAvailableSP = gameState.myActiveSP + (gameState.myBonusSP || 0);

    // フィールドに空きがあり、SPで召喚可能なモンスターがある場合は召喚優先
    if (emptySlots > 0) {
      const summonableNow = submergibleCards.filter(c =>
        c.type === 'monster' && (c.cost || 0) <= totalAvailableSP
      );
      // 今すぐ召喚できるカードがあれば潜航しない
      if (summonableNow.length > 0) return null;
    }

    // SPが不足しているカードを潜航候補に
    const highCostCards = submergibleCards.filter(c =>
      c.type === 'monster' && (c.cost || 0) > totalAvailableSP
    );

    if (highCostCards.length > 0) {
      // コストが高いものから潜航（深度ボーナスで強化される）
      const sorted = [...highCostCards].sort((a, b) => (b.cost || 0) - (a.cost || 0));
      return sorted[0];
    }

    // フィールドが満杯の場合は低コストでも潜航
    if (emptySlots === 0) {
      // 攻撃力が高いものを潜航（深度ボーナスで更に強化）
      const sorted = [...submergibleCards].sort((a, b) => (b.attack || 0) - (a.attack || 0));
      return sorted[0];
    }

    return null;
  },

  /**
   * 浮上するカードとスロットを選択
   *
   * 戦略:
   * - 深度3以上で浮上（召喚酔いなし + ATK+600以上のボーナス）
   * - 深度5以上は浮上効果も発動するため優先
   * - 同深度帯では効率（ATK+ボーナス/コスト）が高いカードを優先
   */
  chooseSurface(surfaceableCards: DeepSeaCard[], gameState: AIGameState): { deepSeaCard: DeepSeaCard; fieldSlot: number } | null {
    if (surfaceableCards.length === 0) return null;

    const emptySlots = gameState.myField
      .map((m, i) => m === null ? i : -1)
      .filter(i => i >= 0);

    if (emptySlots.length === 0) return null;

    // 効率計算: (ATK + 深度ボーナス) / 浮上コスト
    const DEPTH_ATK_BONUS = 200;
    const calcEfficiency = (c: DeepSeaCard): number => {
      const surfaceCost = Math.max(1, (c.card.cost || 0) - c.depth);
      const atkWithBonus = (c.card.attack || 0) + (c.depth * DEPTH_ATK_BONUS);
      return atkWithBonus / surfaceCost;
    };

    // 深度3以上のカードを優先的に浮上（召喚酔い回避）
    const deepCards = surfaceableCards.filter(c => c.depth >= 3);

    if (deepCards.length > 0) {
      // 深度5以上は浮上効果発動、その中で最も効率が高いものを選択
      const surfaceEffectCards = deepCards.filter(c => c.depth >= 5);
      if (surfaceEffectCards.length > 0) {
        const sorted = [...surfaceEffectCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
        return {
          deepSeaCard: sorted[0],
          fieldSlot: emptySlots[0],
        };
      }

      // 深度3-4で最も効率が高いものを選択
      const sorted = [...deepCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
      return {
        deepSeaCard: sorted[0],
        fieldSlot: emptySlots[0],
      };
    }

    // フィールドにモンスターがいない場合も深度3以上で浮上（召喚酔い回避）
    const hasMonster = gameState.myField.some(m => m !== null);
    if (!hasMonster) {
      const emergencyCards = surfaceableCards.filter(c => c.depth >= 3);
      if (emergencyCards.length > 0) {
        const sorted = [...emergencyCards].sort((a, b) => calcEfficiency(b) - calcEfficiency(a));
        return {
          deepSeaCard: sorted[0],
          fieldSlot: emptySlots[0],
        };
      }
    }

    // 深度2以下は召喚酔いがあるため浮上しない
    return null;
  },
};
